import { useCallback } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, VStack } from '@primerai/thread';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { ReactFormPassword } from '../../components/form/input';
import { useValidationSchema } from './helpers';
import { RequirementsFeedback } from './Requirements';
import { PasswordMeter } from '~/components/password/PasswordMeter';

import { ConfigType } from '~/components/password/types';
import { doPasswordsMatch } from '~/components/password/validator';
import { getIsLoadingResetPassword } from '~/store/password/selectors';
import { get } from '~/utils/object';

interface Props {
  onSubmit: (pw: string) => void;
  currentRuleSet: ConfigType;
}

type FormValues = {
  newPassword: string;
  confirmedPassword: string;
};

const initialValues: FormValues = {
  newPassword: '',
  confirmedPassword: '',
};

const PasswordResetForm = (props: Props) => {
  const { currentRuleSet, onSubmit } = props;
  const isLoading = useSelector(getIsLoadingResetPassword);
  const usePasswordMeter = get(currentRuleSet, 'usePasswordStrength', false);
  const validationSchema = useValidationSchema(currentRuleSet);

  const {
    handleSubmit,
    control,
    formState: { errors },
    watch,
  } = useForm<FormValues>({
    defaultValues: initialValues,
    resolver: yupResolver(validationSchema) as any,
  });

  const newPassword = watch('newPassword', initialValues.newPassword);
  const confirmedPassword = watch(
    'confirmedPassword',
    initialValues.confirmedPassword,
  );

  const onSubmitValidForm: SubmitHandler<FormValues> = useCallback(
    ({ newPassword: password }) => {
      if (password) {
        onSubmit(password);
      }
    },
    [onSubmit],
  );

  const onSubmitHandler = (e: React.SyntheticEvent) => {
    e.preventDefault();
    void handleSubmit(onSubmitValidForm)(e);
  };

  return (
    <form onSubmit={onSubmitHandler}>
      <VStack gap="$6">
        <ReactFormPassword
          control={control}
          errors={errors}
          name="newPassword"
          placeholder="New Password"
        />
        <ReactFormPassword
          control={control}
          errors={errors}
          name="confirmedPassword"
          placeholder="Re-enter new Password"
        />
        {usePasswordMeter ? (
          <Box py="$6">
            <PasswordMeter passwordLength={newPassword?.length ?? 0} />
          </Box>
        ) : (
          <RequirementsFeedback
            currentRuleSet={currentRuleSet}
            password={newPassword ?? ''}
            passwordsMatch={doPasswordsMatch(
              newPassword ?? '',
              confirmedPassword ?? '',
            )}
          />
        )}
        <Button
          appearance="primary"
          isLoading={isLoading}
          type="submit"
          width="100%"
        >
          Change password
        </Button>
      </VStack>
    </form>
  );
};

export default PasswordResetForm;
